import { BaseField } from "./field";
import { Organization } from "./organization";
import { User } from "./user";

export interface CropCycleReport {
    id: number;
    start_date: string;
    end_date: string;
    planned_total_nitrogen: number | null;
    yield_goal: number | null;
    crop: string | null;
    planting_date: string | null;
    planting_operation: number | null;
    n_applied: number | null;
    si_min: number | null;
    si_mean: number | null;
    harvest_date: string | null;
    average_yield: number | null;
    nue: number | null;
    average_h2o_sufficiency: number | null;
    total_irrigation: number | null;
    previous_crop: number | null;
    manure_use: boolean | null;
    cover_crop: string | null;
    cover_crop_termination: string | null;
    inhibitor_use: boolean | null;
    eef_use: boolean | null;
    tillage_practice: string | null;
    enrollment_type: string | null;
}

export interface CropCycleReportRow {
    id: number;
    field_id: number;
    name: string;
    typical_N_applied_pounds_per_acre: number | null;
    typical_yield_bushels_per_acre: number | null;
    soil_configuration: number | null;
    soil_organic_matter: number | null;
    has_drainage: boolean | null;
    grower: string | null;
    service_provider: string | null;
    account_manager: string | null;
    insights_active: boolean;
    active_enrollment_tier: number | null;
    enrollment_type: string | null;
    crop_cycle: CropCycleReport;
}

export type FieldReportTableRow = Omit<
    BaseField,
    | "active_enrollment"
    | "active_planting_operation"
    | "irrigation_system_configuration"
    | "has_drainage"
> & {
    active_enrollment: BaseField["active_enrollment"] | null;
    typical_N_applied_pounds_per_acre: number | null;
    typical_yield_bushels_per_acre: number | null;
    soil_configuration: number | null;
    soil_organic_matter: number | null;
    has_drainage: boolean | null;
    cropCycle: CropCycleReport | null;
    rowId: string;
};

export type FieldReportLookups = {
    growersById: Record<number, Organization>;
    serviceProvidersById: Record<number, Organization>;
    accountManagersById: Record<number, User>;
};

function findIdByName(
    byId: Record<number, { id: number; name?: string; full_name?: string }>,
    name: string | null | undefined,
    getName: (item: {
        id: number;
        name?: string;
        full_name?: string;
    }) => string | undefined | null,
): number {
    if (!name) return 0;
    const found = Object.values(byId).find((item) => getName(item) === name);
    return found?.id ?? 0;
}

export function toFieldReportTableRow(
    row: CropCycleReportRow,
    lookups: FieldReportLookups,
): FieldReportTableRow {
    const growerId = findIdByName(
        lookups.growersById,
        row.grower,
        (item) => item.name,
    );
    const serviceProviderId = findIdByName(
        lookups.serviceProvidersById,
        row.service_provider,
        (item) => item.name,
    );
    const accountManagerId = findIdByName(
        lookups.accountManagersById,
        row.account_manager,
        (item) => item.full_name,
    );

    return {
        id: row.field_id,
        name: row.name,
        grower: growerId,
        service_provider: serviceProviderId,
        account_manager: accountManagerId,
        typical_N_applied_pounds_per_acre:
            row.typical_N_applied_pounds_per_acre ?? null,
        typical_yield_bushels_per_acre:
            row.typical_yield_bushels_per_acre ?? null,
        soil_configuration: row.soil_configuration ?? null,
        soil_organic_matter: row.soil_organic_matter ?? null,
        has_drainage: row.has_drainage ?? null,
        active_enrollment:
            row.active_enrollment_tier != null
                ? {
                      id: 0,
                      enrollment_tier: row.active_enrollment_tier,
                      insights_active: row.insights_active,
                  }
                : null,
        cropCycle: row.crop_cycle,
        rowId: `${row.field_id}-${row.crop_cycle.id}`,
    };
}
